import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Box,
  Chip,
  Icon,
  InlineMessage,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_SEC_MED,
  l,
  MAX_CHIP_LENGTH,
  type MessageState,
  VALID_CHIP_PATTERN,
} from '../../../../../shared/constants';

import './TypedChipInput.css';

const MAX_CHIPS = 5;
const DEFAULT_IP_VALIDATION = {
  isCritical: false,
  message: '',
};

interface TypedChipInputProps {
  isDisabled?: boolean;
  currentTags: string[];
  setNewTags: (newTags: string[]) => void;
}

/**
 * Inline chip input that renders existing tags as removable chips and
 * allows adding new ones by typing and pressing Enter, Space, or comma, up to a maximum of 5.
 * Comma-separated input adds multiple chips at once.
 * Duplicate values are ignored.
 * @param props - {@link TypedChipInputProps}
 * @param props.isDisabled - Disables chip removal and the input field when true.
 * @param props.currentTags - Initial set of tag strings to display as chips.
 * @param props.setNewTags - Callback invoked with the updated tags array when a chip is added or removed.
 * @returns The chip input component.
 */
function TypedChipInput({
  isDisabled,
  currentTags,
  setNewTags,
}: TypedChipInputProps) {
  const intl = useIntl();

  // States
  const [input, setInput] = useState('');
  const [inputValidation, setInputValidation] = useState(DEFAULT_IP_VALIDATION);
  const [chips, setChips] = useState<string[]>([]);

  useEffect(() => {
    setChips(currentTags);
  }, [currentTags]);

  /** Validates a single tag value and returns an error message, or null if valid. */
  const validateChip = (
    value: string,
    currentChips: string[]
  ): string | null => {
    if (!value) {
      return null;
    }

    if (value.length > MAX_CHIP_LENGTH) {
      return `Text must be ${MAX_CHIP_LENGTH} characters or fewer`;
    }

    if (!VALID_CHIP_PATTERN.test(value)) {
      return 'Only letters, numbers, underscores, colon and hyphens are allowed';
    }

    if (currentChips.includes(value)) {
      return 'Chip already added';
    }

    return null;
  };

  /**
   * Adds one or more chip values, stopping at MAX_CHIPS.
   * Returns the last validation error encountered, or null.
   */
  const addChips = (values: string[]): string | null => {
    let lastError: string | null = null;
    let updatedChips = [...chips];

    values.forEach((value) => {
      if (updatedChips.length >= MAX_CHIPS) {
        return;
      }

      const error = validateChip(value, updatedChips);

      if (error) {
        lastError = error;

        return;
      }

      updatedChips = [...updatedChips, value];
    });

    if (updatedChips.length !== chips.length) {
      setChips(updatedChips);
      setNewTags(updatedChips);
    }

    return lastError;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();

      const trimmed = input.trim();

      if (!trimmed) {
        return;
      }

      const error = addChips([trimmed]);

      if (error) {
        setInputValidation({
          isCritical: true,
          message: error,
        });

        return;
      }

      setInputValidation(DEFAULT_IP_VALIDATION);
      setInput('');
    }
  };

  /** Handles comma-separated input by splitting and adding each segment as a chip. */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValidation(DEFAULT_IP_VALIDATION);

    const { value } = e.target;

    if (!value.includes(',')) {
      setInput(value);

      return;
    }

    // Split on commas, trim each segment, and filter out empty strings
    const segments = value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (segments.length === 0) {
      setInput('');

      return;
    }

    const error = addChips(segments);

    if (error) {
      setInputValidation({
        isCritical: true,
        message: intl.formatMessage(l[error as MessageState]),
      });
    }

    setInput('');
  };

  const deleteChip = (index: number) => {
    const newTags = chips.filter((_, i) => i !== index);

    setChips(newTags);
    setNewTags(newTags);
  };

  return (
    <Box tag='div' className='TypedChipInput-wrapper'>
      <Box
        tag='div'
        className={cx(
          'TypedChipInput-container',
          isDisabled && 'TypedChipInput-container--isDisabled'
        )}>
        <div className='TypedChipInput-chipList'>
          {chips.map((chip, idx) => (
            <Chip
              // eslint-disable-next-line react/no-array-index-key
              key={idx}
              size='small'
              label={
                <Typography
                  kind='body3'
                  color={CF_SEC_MED}
                  className='TypedChipInput-chipLabel'
                  tooltipConfigs={{ isPortalEnabled: true }}
                  isNoWrap
                  lineLimit={1}>
                  {chip}
                </Typography>
              }
              isDisabled={isDisabled}
              rightIcon={
                <span
                  role='button'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();

                      if (!isDisabled) {
                        deleteChip(idx);
                      }
                    }
                  }}
                  onClick={() => {
                    if (!isDisabled) {
                      deleteChip(idx);
                    }
                  }}
                  tabIndex={0}>
                  <Icon kind='CloseCircle' size='xSmall' />
                </span>
              }
              className='TypedChipInput-chip'
            />
          ))}
        </div>
        {!isDisabled && chips.length < MAX_CHIPS && (
          <input
            value={input}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={intl.formatMessage({
              id: 'F/Js6k',
              defaultMessage: 'Type a value and press enter to add.',
              description:
                'Placeholder text for input field in TypedChipInput component',
            })}
            className='TypedChipInput-inputContainer'
          />
        )}
      </Box>
      {inputValidation.isCritical && (
        <InlineMessage
          text={inputValidation.message}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      )}
    </Box>
  );
}

TypedChipInput.defaultProps = {
  isDisabled: false,
};

export default TypedChipInput;
